<template>
  <div
    class="address-search-input-wrapper"
    :class="[wrapperClass, { 'address-search-input-wrapper--invalid': invalid }]"
    :data-theme="effectiveTheme"
  >
    <div
      v-show="!initFailed"
      :id="containerId"
      class="address-search-input w-full"
      :class="[
        {
          'address-search-input--loaded': loaded,
          'address-search-input--has-clear': showsClearButton
        },
        sizeClass
      ]"
    />

    <input
      v-if="initFailed"
      ref="fallbackInputRef"
      :value="fallbackText"
      type="text"
      :placeholder="placeholder"
      :aria-label="placeholder"
      class="address-search-input address-search-input--fallback w-full"
      :class="[sizeClass, { 'address-search-input--has-clear': showsClearButton }]"
      @input="handleFallbackInput"
      @change="handleFallbackCommit"
      @keydown.enter="handleFallbackEnter"
      @compositionstart="handleCompositionStart"
      @compositionend="handleCompositionEnd"
    />

    <p
      v-if="fallbackTextOverlong"
      role="alert"
      class="dark:text-danger-400 mt-2 text-danger-600 text-sm"
    >
      {{ $t('common.address.tooLong') }}
    </p>

    <p
      v-if="initFailed"
      role="status"
      class="dark:text-content-level-300 mt-2 text-content-level-700 text-sm"
    >
      {{ $t('common.address.suggestionsUnavailable') }}
    </p>

    <p
      v-if="providerUnavailable"
      role="alert"
      class="dark:text-danger-400 mt-2 text-danger-600 text-sm"
    >
      {{ $t('common.address.searchUnavailable') }}
    </p>

    <UAlert
      v-if="addressUnresolvedWarningVisible"
      class="mt-2"
      color="warning"
      variant="subtle"
      orientation="horizontal"
      :title="$t('common.address.notVerifiedTitle')"
      :description="$t('common.address.notVerifiedDescription')"
      :actions="unresolvedAddressActions"
    />

    <button
      v-if="showsClearButton"
      v-show="hasValue"
      type="button"
      class="address-search-input__clear"
      :aria-label="$t('common.actions.clear')"
      @click="handleClear"
    >
      <span aria-hidden="true">×</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRef,
  unref,
  useId,
  useTemplateRef,
  watch
} from 'vue'
import { UAlert } from '#components'
import type { AddressSearchTypeValue } from '~/lib/geo-providers/types'
import type {
  AutocompleteInstance,
  AutocompleteProviderConfig,
  ProviderAddressResponse,
  AddressSearchValue
} from '~/lib/geo-providers/interfaces'
import { isLocatableAddressText } from '~/lib/geo-providers/isLocatableAddressText'
import { isOverlongAddressText } from '~/lib/geo-providers/isOverlongAddressText'
import { mapProviderResponseToSearchValue } from '~/lib/geo-providers/mapProviderResponseToSearchValue'
import { resolveTypes } from '~/lib/geo-providers/resolveTypes'
import { resolveProvider } from '~/lib/geo-providers/resolveProvider'
import { getProviderConfig } from '~/composables/getProviderConfig'
import { geocodeAddress } from '~/composables/geocodeAddress'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useLogger } from '~/composables/useLogger'

// --- Props & emits ---

const props = withDefaults(
  defineProps<{
    /** Current address field value. */
    modelValue?: AddressSearchValue | null
    /** Input type used for address lookup. */
    type?: AddressSearchTypeValue | AddressSearchTypeValue[] | undefined
    /** Placeholder shown when the field is empty. */
    placeholder?: string
    /** Optional input element identifier. */
    id?: string | undefined
    /** Disables address entry and lookup. */
    disabled?: boolean
    /** Classes applied to the outer wrapper. */
    wrapperClass?: string
    /** Visual size variant for the input. */
    size?: 'sm' | 'md' | 'lg'
    /** Visual theme variant for the input. */
    theme?: 'light' | 'dark' | undefined
    /** Typed address text the fallback input shows; the owner commits and clears it. */
    typedText?: string
    /** Real invalid-state visual treatment; the owner drives this from its own field validation. */
    invalid?: boolean
  }>(),
  {
    disabled: false,
    id: undefined,
    invalid: false,
    modelValue: null,
    placeholder: 'Enter location',
    size: 'sm',
    theme: undefined,
    type: undefined,
    typedText: '',
    wrapperClass: ''
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: AddressSearchValue | null]
  clear: []
  fallbackClear: []
  fallbackCommit: [value: string]
  fallbackInput: [value: string]
  retrieveError: []
  typedText: [value: string]
  /** A real provider lookup ran for the given typed-but-unpicked text and found no match. */
  addressUnresolved: [text: string]
  /** A real provider lookup ran for the given typed-but-unpicked text and resolved it. */
  addressResolved: [text: string]
  /** The user accepted the unresolved text as the address anyway, from this widget's own prompt. */
  addressConfirmed: [text: string]
}>()

const logger = useLogger('AddressSearchInput')
const t = usePresentationTranslator()

// Re-export for backward compat (10 consumer components depend on this)
export type {
  AddressSearchType,
  AddressSearchTypeValue,
  AddressSearchMetaType
} from '~/lib/geo-providers/types'
export type { AddressSearchValue } from '~/lib/geo-providers/interfaces'

// --- Size variant ---

const sizeClass = computed(() => `address-search-input--${props.size}`)

// --- Clear button capability (set once the resolved provider loads) ---

const providerRendersClearButton = ref(true)

// --- Effective theme (prop overrides the color-mode preference) ---
const colorMode = useColorMode()
const theme = toRef(props, 'theme')
const id = toRef(props, 'id')
const effectiveTheme = computed<'light' | 'dark'>(
  () => theme.value ?? (colorMode.value === 'dark' ? 'dark' : 'light')
)

// --- State (SSR-safe ID via Vue's useId) ---

const containerId = unref(id) ?? useId()
const instance = ref<AutocompleteInstance | null>(null)
const loaded = ref(false)
const typedText = toRef(props, 'typedText')
const hasValue = ref(false)
const clearingInternally = ref(false)
const fallbackInputRef = useTemplateRef<HTMLInputElement>('fallbackInputRef')
let isInitialized = false
// The last selected address text, so a subsequent edit can be told apart from a re-render echo.
let selectedText = ''
// An internally emitted null must not read as an external reset that rebuilds the widget.
let emittedNull = false

// Text of the last focus-driven search; null once focus leaves the widget.
let lastSearchedText: string | null = null
let initInFlight: Promise<void> | null = null
let initGeneration = 0
let retryTimer: ReturnType<typeof setTimeout> | null = null

function clearModelValue(): void {
  emittedNull = props.modelValue !== null
  emit('update:modelValue', null)
  nextTick(() => {
    emittedNull = false
  }).catch(() => undefined)
}

function getAutocompleteText(): string {
  return instance.value?.getValue().trim() ?? ''
}

// --- Provider initialization ---
// A geocoding widget chunk can fail to load right after a deploy (stale CDN
// cache, transient network blip), which otherwise leaves the container
// permanently empty with zero focusable elements. One retry recovers most
// transient failures; if the widget still isn't up after that, initFailed
// switches the template to a plain fallback input so a real input always exists.

const INIT_RETRY_DELAY_MS = 300
const initFailed = ref(false)
const providerUnavailable = ref(false)
const fallbackText = ref('')

const showsClearButton = computed(
  () => initFailed.value || (!providerRendersClearButton.value && props.size === 'sm')
)

const fallbackTextOverlong = computed(() => isOverlongAddressText(fallbackText.value))

// Enter and blur both commit; one edit commits once.
let committedFallbackText = ''

watch(
  typedText,
  (text) => {
    fallbackText.value = text
    committedFallbackText = text
    hasValue.value = text.trim() !== ''
  },
  { immediate: true }
)

const handleFallbackCommit = (event: Event) => {
  const value = event.target instanceof HTMLInputElement ? event.target.value : ''
  if (isLocatableAddressText(value) && value !== committedFallbackText) {
    committedFallbackText = value
    emit('fallbackCommit', value)
  }
}

// Enter that confirms an IME composition is not a commit; Safari fires compositionend before that
// keydown, so the composing flag clears on the next task.
let composing = false

const handleCompositionStart = (): void => {
  composing = true
}

const handleCompositionEnd = (): void => {
  setTimeout(() => {
    composing = false
  }, 0)
}

const handleFallbackEnter = (event: KeyboardEvent) => {
  if (event.isComposing || composing) return
  handleFallbackCommit(event)
}

const handleFallbackInput = (event: Event) => {
  const value = event.target instanceof HTMLInputElement ? event.target.value : ''
  fallbackText.value = value
  committedFallbackText = ''
  hasValue.value = value.trim() !== ''

  if (value.trim() === '') {
    emit('typedText', '')
    emit('fallbackClear')
    emit('clear')
    return
  }

  emit('fallbackInput', value)
}

function handleProviderUnavailable(reason: string): void {
  if (providerUnavailable.value) return
  providerUnavailable.value = true
  logger.warn('handleProviderUnavailable', { reason })
}

function handleProviderAvailable(): void {
  providerUnavailable.value = false
}

// Wires the provider widget's events and seeds its initial value.
function bindInstanceListeners(widget: AutocompleteInstance): void {
  // Listen for address selection
  widget.addEventListener('retrieve', (e: CustomEvent<ProviderAddressResponse>) => {
    const detail = e.detail
    if (!detail?.lat || !detail?.lng) {
      emit('retrieveError')
      return
    }

    invalidateUnresolvedAddressLookup()
    emit('typedText', '')
    emit('update:modelValue', mapProviderResponseToSearchValue(detail))
    nextTick(() => {
      selectedText = getAutocompleteText()
    }).catch(() => undefined)
  })

  // Listen for clear event from adapter
  widget.addEventListener('clear', () => {
    invalidateUnresolvedAddressLookup()
    emit('typedText', '')
    emit('clear')
    clearModelValue()
  })

  // A value without coordinates (stored text, or an edit not yet picked) re-opens suggestions on focus
  const widgetElement = widget.getElement()
  widgetElement?.addEventListener('focusin', handleWidgetFocus)
  widgetElement?.addEventListener('focusout', handleWidgetBlur)

  // Fallback: detect manual input clear (Google adapter may not fire 'clear')
  watchInputClear()

  // Set initial value if modelValue already has an address
  const initialAddress = props.modelValue?.address
  if (initialAddress !== undefined && initialAddress !== '') {
    widget.setValue?.(initialAddress)
    selectedText = initialAddress
  }
}

function buildProviderConfig(): AutocompleteProviderConfig {
  const { provider: config } = getProviderConfig()
  const types = resolveTypes(props.type)
  return {
    accessToken: config.accessToken,
    logger,
    name: config.name,
    onAvailable: handleProviderAvailable,
    onUnavailable: handleProviderUnavailable,
    size: props.size ?? 'sm',
    theme: effectiveTheme.value,
    ...(types ? { types } : {})
  }
}

function scheduleInitRetry(): void {
  retryTimer = setTimeout(() => {
    retryTimer = null
    initAutocomplete(1).catch(() => undefined)
  }, INIT_RETRY_DELAY_MS)
}

function initAutocomplete(attempt = 0): Promise<void> {
  if (props.disabled || isInitialized) return Promise.resolve()
  initInFlight ??= runInit(attempt).finally(() => {
    initInFlight = null
  })
  return initInFlight
}

function adoptInstance(autocompleteInstance: AutocompleteInstance): void {
  instance.value = autocompleteInstance
  isInitialized = true
  loaded.value = true
  initFailed.value = false
  providerUnavailable.value = false

  bindInstanceListeners(autocompleteInstance)
}

function handleInitFailure(error: unknown, attempt: number): void {
  if (attempt === 0) {
    scheduleInitRetry()
    return
  }

  logger.warn('initAutocomplete', { error })
  initFailed.value = true
}

async function createInstance(generation: number): Promise<AutocompleteInstance | null> {
  const provider = await resolveProvider()
  if (generation !== initGeneration) return null
  providerRendersClearButton.value = provider.rendersClearButton

  const autocompleteInstance = await provider.create(
    containerId,
    props.placeholder ?? 'Enter location',
    buildProviderConfig()
  )
  if (generation !== initGeneration) {
    autocompleteInstance?.destroy()
    return null
  }

  if (!autocompleteInstance) throw new Error('Provider returned no autocomplete instance')
  return autocompleteInstance
}

async function runInit(attempt: number): Promise<void> {
  initGeneration += 1
  const generation = initGeneration
  // Wait for next tick to ensure the container div is in the DOM
  await nextTick()

  if (document.getElementById(containerId) === null) {
    logger.warn('initAutocomplete', { containerId, reason: 'container not found in DOM' })
    return
  }

  try {
    const created = await createInstance(generation)
    if (created !== null) adoptInstance(created)
  } catch (error) {
    if (generation === initGeneration) handleInitFailure(error, attempt)
  }
}

// --- Re-open suggestions ---

function hasCoordinates(value: AddressSearchValue | null): boolean {
  return value !== null && value.lat !== 0 && value.lng !== 0
}

function handleWidgetFocus() {
  if (hasCoordinates(props.modelValue)) return
  const text = getAutocompleteText()
  if (text === lastSearchedText) return
  lastSearchedText = text
  instance.value?.search?.()
}

// A real lookup ran for this text and found no match -- warn and let the user choose, rather
// than block (suggestions are a convenience, never a hard gate).
const addressUnresolvedWarningVisible = ref(false)
// The exact text the pending/last lookup queried, so a consumer can ignore a stale result for
// text the user has since edited away from.
const unresolvedQueryText = ref('')
// Bumped by any edit, clear, or pick so a lookup that resolves after the text it queried no
// longer applies -- including after a later successful pick -- is discarded, not applied.
let addressLookupGeneration = 0

/** Any edit, clear, or pick invalidates whatever unresolved-address lookup was pending or shown. */
function invalidateUnresolvedAddressLookup(): void {
  addressLookupGeneration += 1
  addressUnresolvedWarningVisible.value = false
}

// Suggestions are a convenience, never a hard gate (spec: picking one is optional). This only
// runs a real lookup, when one is available, to tell a genuine no-match apart from lookup being
// down -- lookup being unavailable here must never surface as an error.
async function confirmTypedAddressResolution(text: string): Promise<void> {
  if (initFailed.value || providerUnavailable.value) return
  if (text.trim() === '') return

  addressLookupGeneration += 1
  const generation = addressLookupGeneration

  const outcome = await geocodeAddress(text)
  if (generation !== addressLookupGeneration) return

  if (outcome.status === 'noMatch') {
    addressUnresolvedWarningVisible.value = true
    unresolvedQueryText.value = text
    emit('addressUnresolved', text)
  } else if (outcome.status === 'resolved') {
    addressUnresolvedWarningVisible.value = false
    emit('addressResolved', text)
  }
}

/** The user accepts the typed text as the address despite the failed lookup. */
function acceptUnresolvedAddress(): void {
  addressUnresolvedWarningVisible.value = false
  emit('addressConfirmed', unresolvedQueryText.value)
}

/** The user wants to fix the address instead; dismiss the warning and return focus to the field. */
function editUnresolvedAddress(): void {
  addressUnresolvedWarningVisible.value = false
  focus()
}

const unresolvedAddressActions = computed(() => [
  {
    color: 'neutral' as const,
    label: t('common.address.useAnyway'),
    onClick: acceptUnresolvedAddress,
    variant: 'subtle' as const
  },
  {
    color: 'neutral' as const,
    label: t('common.address.editAddress'),
    onClick: editUnresolvedAddress,
    variant: 'ghost' as const
  }
])

function handleWidgetBlur(event: FocusEvent) {
  const widgetElement = instance.value?.getElement()
  const staysInWidget =
    event.relatedTarget instanceof Node && widgetElement?.contains(event.relatedTarget) === true
  if (staysInWidget) return
  lastSearchedText = null

  if (hasCoordinates(props.modelValue)) return
  const text = getAutocompleteText()
  if (text !== '' && text !== selectedText) {
    confirmTypedAddressResolution(text).catch(() => undefined)
  }
}

// --- Input clear fallback ---

function watchInputClear() {
  const widgetElement = instance.value?.getElement()
  if (!widgetElement) return

  widgetElement.addEventListener('input', () => {
    const currentValue = getAutocompleteText()
    hasValue.value = !!currentValue
    invalidateUnresolvedAddressLookup()
    if (currentValue !== selectedText && !clearingInternally.value) {
      selectedText = ''
      emit('typedText', currentValue)
      emit('clear')
      clearModelValue()
    }
  })
}

function clearFallback(): void {
  committedFallbackText = ''
  fallbackText.value = ''
  hasValue.value = false
  emit('typedText', '')
  emit('fallbackClear')
  emit('clear')
  clearModelValue()
}

function handleClear() {
  if (initFailed.value) {
    clearFallback()
    return
  }
  if (!instance.value) return
  invalidateUnresolvedAddressLookup()
  clearingInternally.value = true
  instance.value.setValue?.('')
  hasValue.value = false
  selectedText = ''
  emit('typedText', '')
  emit('clear')
  clearModelValue()
  nextTick(() => {
    clearingInternally.value = false
  }).catch(() => undefined)
}

function focus(): void {
  if (initFailed.value) {
    fallbackInputRef.value?.focus()
    return
  }
  instance.value?.focus()
}

defineExpose({ focus })

// --- Cleanup ---

function destroyInstance() {
  instance.value?.destroy()
  instance.value = null
  isInitialized = false
  lastSearchedText = null
  // A rebuild discards the widget a pending lookup was querying text out of.
  invalidateUnresolvedAddressLookup()
}

// --- Lifecycle ---

onMounted(() => {
  initAutocomplete().catch(() => undefined)
})

onUnmounted(() => {
  initGeneration += 1
  if (retryTimer !== null) clearTimeout(retryTimer)
  retryTimer = null
  destroyInstance()
})

// --- External modelValue changes ---

watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    const isEmittedNullEcho = emittedNull && newVal === null
    emittedNull = false
    if (isEmittedNullEcho) return

    const address = newVal?.address ?? ''
    hasValue.value = address !== ''
    fallbackText.value = address
    if (!instance.value) return

    if (oldVal && !newVal) {
      destroyInstance()
      initAutocomplete().catch(() => undefined)
    } else if (newVal?.address !== undefined && newVal.address !== '') {
      instance.value.setValue?.(newVal.address)
      selectedText = newVal.address
    }
  }
)

// --- Color-mode changes: the provider takes its theme once at creation ---
watch(effectiveTheme, () => {
  if (!instance.value) return
  destroyInstance()
  initAutocomplete().catch(() => undefined)
})
</script>

<style scoped>
/* Default Mapbox/Google autocomplete styles — :deep() so they apply to provider-injected elements */
.address-search-input {
  min-height: 0;
}

/* Size variants — drive inner height, font-size, icon, and padding via CSS vars
   so the same value is reused across Mapbox (legacy, SearchBox web component)
   and Google (gmp-place-autocomplete ::part) selectors. */
.address-search-input--sm {
  --asi-height: 36px;
  --asi-font-size: 14px;
  --asi-padding-left: 32px;
  --asi-padding-right: 40px;
  --asi-icon-size: 16px;
  --asi-icon-left: 10px;
}

.address-search-input--md {
  --asi-height: 50px;
  --asi-font-size: 16px;
  --asi-padding-left: 40px;
  --asi-padding-right: 44px;
  --asi-icon-size: 18px;
  --asi-icon-left: 12px;
}

.address-search-input--lg {
  --asi-height: 60px;
  --asi-font-size: 18px;
  --asi-padding-left: 48px;
  --asi-padding-right: 48px;
  --asi-icon-size: 22px;
  --asi-icon-left: 14px;
}

.address-search-input--loaded {
  min-height: var(--asi-height, 50px);
  background-color: var(--color-base-100, #ffffff);
  border-radius: 0.5rem;
}

/* Fallback plain input, shown only if the geocoding widget fails to load. */
.address-search-input--fallback {
  height: var(--asi-height, 50px);
  min-height: var(--asi-height, 50px);
  font-size: var(--asi-font-size, 16px);
  padding-left: var(--asi-padding-left, 40px);
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: #ffffff;
  color: #1f2937;
}

.address-search-input--fallback:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--brand-primary-light, #605dff);
}

/* The clear button overlaps the input's own border unless padding reserves its width. */
.address-search-input--fallback.address-search-input--has-clear {
  padding-right: var(--asi-padding-right, 40px);
}

/* Mapbox SearchBox web component */
.address-search-input > :deep(.mapbox-search-box),
.address-search-input > :deep(mapbox-search-box) {
  width: 100% !important;
  min-height: var(--asi-height, 50px) !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  --border: none !important;
}

/* Mapbox — light mode border + shadow on the SearchBox element */
:root:not([data-theme='dark'])
  .address-search-input
  > :deep(mapbox-search-box)
  > [class$='--SearchBox'],
:root:not([data-theme='dark'])
  .address-search-input
  > :deep(.mapbox-search-box)
  > [class$='--SearchBox'] {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.address-search-input > :deep(.mapbox-search-box) input,
.address-search-input > :deep(mapbox-search-box) input,
.address-search-input > :deep(.mapbox-search-box)::part(input),
.address-search-input > :deep(mapbox-search-box)::part(input),
.address-search-input > :deep(.mapboxgl-ctrl-geocoder) input {
  height: var(--asi-height, 50px) !important;
  min-height: var(--asi-height, 50px) !important;
  border: none !important;
  border-radius: 0.5rem;
  outline: none !important;
  font-size: var(--asi-font-size, 16px) !important;
}

/* Mapbox GL Geocoder (legacy) */
.address-search-input > :deep(.mapboxgl-ctrl-geocoder) {
  width: 100% !important;
  min-height: var(--asi-height, 50px) !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

/* Google PlaceAutocompleteElement (new API) */
.address-search-input > :deep(gmp-place-autocomplete) {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
  min-height: var(--asi-height, 50px) !important;
  background: #ffffff !important;
  color-scheme: light !important;
  font-family: 'Inter', system-ui, sans-serif !important;
  font-size: var(--asi-font-size, 16px) !important;
  line-height: 1.4 !important;
  color: #1f2937 !important;
}

/* Size sm — Google: the host element has its own intrinsic sizing that
   ignores min-height, so we force an explicit height. Same for the clear
   button so it stays aligned with the host. */
.address-search-input--sm > :deep(gmp-place-autocomplete) {
  height: 38px !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(container),
.address-search-input > :deep(gmp-place-autocomplete)::part(input-container) {
  background: #ffffff !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(input) {
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  height: var(--asi-height, 50px) !important;
  min-height: var(--asi-height, 50px) !important;
  border: none !important;
  border-radius: 0.5rem !important;
  outline: none !important;
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: #1f2937 !important;
  padding: 0 12px 0 var(--asi-padding-left, 40px) !important;
  background-color: #ffffff !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%231f2937' stroke-width='1.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M15 10.5a3 3 0 11-6 0 3 3 0 016 0z'/%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: var(--asi-icon-left, 12px) center !important;
  background-size: var(--asi-icon-size, 18px) var(--asi-icon-size, 18px) !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}

/* The clear button overlaps the input's own border unless padding reserves its width. */
.address-search-input--has-clear > :deep(gmp-place-autocomplete)::part(input) {
  padding-right: var(--asi-padding-right, 40px) !important;
}

/* Google provider — light mode border on the host element */
:root:not([data-theme='dark']) .address-search-input > :deep(gmp-place-autocomplete) {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Remove box-shadow for size sm in light mode */
:root:not([data-theme='dark']) .address-search-input--sm > :deep(gmp-place-autocomplete) {
  box-shadow: none;
}

/* The focus-ring part is `position: absolute; width: 100%` of the
   input-container, so it tracks the container (wider than the input once
   siblings like the clear button take space). We hide it and draw our own
   focus ring via box-shadow on ::part(input):focus instead. */
.address-search-input > :deep(gmp-place-autocomplete)::part(focus-ring) {
  display: none !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(input)::placeholder {
  color: #9ca3af !important;
  opacity: 0.6 !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(input):focus {
  box-shadow: none !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-list) {
  border-radius: 0.5rem !important;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  margin-top: 4px !important;
  border: 1px solid #d1d5db !important;
  background: #ffffff !important;
  color: #1f2937 !important;
  overflow: hidden !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-item) {
  padding: 2px 16px !important;
  border: none !important;
  font-family: 'Inter, system-ui, sans-serif' !important;
  font-size: 16px !important;
  color: #1f2937 !important;
  cursor: pointer !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-item-icon) {
  display: none !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-item-main-text) {
  display: block !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
  font-size: 16px !important;
  line-height: 1.4 !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-item-secondary-text) {
  display: block !important;
  margin-top: 2px !important;
  color: #6b7280 !important;
  opacity: 0.7 !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  font-weight: 400 !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-item-match) {
  font-weight: 700 !important;
}

.address-search-input > :deep(gmp-place-autocomplete)::part(prediction-item-selected) {
  background-color: #f3f4f6 !important;
}

/* Wrapper — position:relative for the sm overlay clear button */
.address-search-input-wrapper {
  position: relative;
}

/* Custom clear button — only rendered for Google provider + size="sm" */
.address-search-input__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  z-index: var(--z-index-ds-inline);
  opacity: 1;
  transition: opacity 0.15s ease;
}

.address-search-input__clear:hover {
  opacity: 0.7;
}

.address-search-input__clear:active {
  opacity: 0.5;
}

/* ---------------------------------------------------------------------------
   Dark mode — driven by `.address-search-input-wrapper[data-theme='dark']`,
   which the component sets from the `theme` prop or, when omitted, from the
   current site theme. Light rules above stay untouched, so the default
   appearance is preserved.
   --------------------------------------------------------------------------- */
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete) {
  background: #1f2937 !important;
  color-scheme: dark !important;
  color: #f3f4f6 !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(container),
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(input-container) {
  background: #1f2937 !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(input) {
  background-color: #1f2937 !important;
  color: #f3f4f6 !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af' stroke-width='1.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M15 10.5a3 3 0 11-6 0 3 3 0 016 0z'/%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z'/%3E%3C/svg%3E") !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(input)::placeholder {
  color: #6b7280 !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(prediction-list) {
  background: #1f2937 !important;
  color: #f3f4f6 !important;
  border: 1px solid #374151 !important;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.4),
    0 2px 4px -1px rgba(0, 0, 0, 0.3) !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(prediction-item) {
  color: #f3f4f6 !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(prediction-item-main-text) {
  color: #f3f4f6 !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(prediction-item-secondary-text) {
  color: #9ca3af !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(prediction-item-selected) {
  background-color: #374151 !important;
}

/* Google host border in dark mode — the :root:not(...) light rule is skipped,
   so we paint a darker border explicitly to keep the box visible. */
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(gmp-place-autocomplete) {
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.4),
    0 2px 4px -1px rgba(0, 0, 0, 0.3);
}

/* Remove box-shadow for size sm in dark mode */
.address-search-input-wrapper[data-theme='dark']
  .address-search-input--sm
  > :deep(gmp-place-autocomplete) {
  box-shadow: none;
}

/* Custom clear button — invert icon color for dark mode */
.address-search-input-wrapper[data-theme='dark'] .address-search-input__clear {
  color: #9ca3af;
}

/* Mapbox — the .Input element ships with a hardcoded `color: rgba(0,0,0,.75)`
   that ignores the theme's `colorText`, so we have to repaint it explicitly
   in dark mode. Placeholder is browser-default otherwise, which is invisible
   on the dark background. */
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(.mapbox-search-box)
  input,
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(mapbox-search-box)
  input {
  color: #f3f4f6 !important;
}

.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(.mapbox-search-box)
  input::placeholder,
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(mapbox-search-box)
  input::placeholder {
  color: #9ca3af !important;
  opacity: 1 !important;
}

/* Mapbox SearchBox border in dark mode */
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(mapbox-search-box)
  > [class$='--SearchBox'],
.address-search-input-wrapper[data-theme='dark']
  .address-search-input
  > :deep(.mapbox-search-box)
  > [class$='--SearchBox'] {
  border-color: rgba(255, 255, 255, 0.2);
}

/* ---------------------------------------------------------------------------
   Invalid state -- driven by the `invalid` prop, which the owning form sets
   from its own fieldMeta/hasMetaErrors verdict, the same mechanism every
   other field in this app uses to decide whether it is in error.
   --------------------------------------------------------------------------- */
.address-search-input-wrapper--invalid .address-search-input--fallback {
  border-color: var(--color-danger-500);
  background-color: var(--color-danger-50);
}

.address-search-input-wrapper--invalid .address-search-input--fallback:focus {
  box-shadow: 0 0 0 2px var(--color-danger-500);
}

.address-search-input-wrapper--invalid
  .address-search-input
  > :deep(gmp-place-autocomplete)::part(input) {
  border-color: var(--color-danger-500) !important;
}

:root:not([data-theme='dark'])
  .address-search-input-wrapper--invalid
  .address-search-input
  > :deep(gmp-place-autocomplete) {
  border-color: var(--color-danger-500) !important;
}

.address-search-input-wrapper--invalid
  .address-search-input
  > :deep(mapbox-search-box)
  > [class$='--SearchBox'],
.address-search-input-wrapper--invalid
  .address-search-input
  > :deep(.mapbox-search-box)
  > [class$='--SearchBox'] {
  border-color: var(--color-danger-500) !important;
}

.address-search-input-wrapper[data-theme='dark'].address-search-input-wrapper--invalid
  .address-search-input
  > :deep(gmp-place-autocomplete) {
  border-color: var(--color-danger-400) !important;
}

.address-search-input-wrapper[data-theme='dark'].address-search-input-wrapper--invalid
  .address-search-input--fallback {
  border-color: var(--color-danger-400);
  background-color: transparent;
}
</style>
